import { produce } from "immer";
import { Store } from "reducers/rootReducer";
import { of, from, Observable } from "rxjs";
import { AxiosRequestConfig, AxiosError } from "axios";
import { APP_ROUTES, HOST, endpoints } from "config";
import { Scopes, loadTokenResource, saveTokenResource, clearMfaAutoSignOut } from "helpers/auth";
import api, { AvamaeDefaultError, BaseAvamaeResponse } from "api";
import { map, tap, switchMap, catchError, ignoreElements, pluck } from "rxjs/operators";
import { combineEpics, ofType, ActionsObservable, StateObservable } from "redux-observable";
import { createSelector } from "reselect";
import { addSeconds } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  DISABLE_AUTO_SIGNIN_KEY,
  AUTO_SIGNIN_DISABLED_TIME_KEY,
  AUTO_SIGNIN_ATTEMPT_IN_PROGRESS_KEY,
  TENANT_ID_KEY,
} from "components/SignIn/SignIn.config";
import { REDIRECT_LOCATION_KEY } from "components/Redirect/Redirect.config";
import {
  MFAChallengeResponse,
  MFADisableResponse,
  MFAEnableResponse,
  MFATypes,
  RequirementsResponse,
  SetPasswordResponse,
  VerificationStep,
  VerifyEmailResponse,
} from "./models";

const UNKOWN_ERROR_MESSAGE =
  "Sorry, something went wrong. Please try again later. If the problem persists, please contact support.";
export type SignInAttempt = {
  username: string;
  password: string;
  rememberMe: boolean;
  host?: string;
};

export type RequirementsToken = {
  token: string;
};

export type MFAChallenge = {
  verificationToken: string;
  challengeType: MFATypes;
  mobilePhoneNumber?: string;
  qrCode?: string;
  sharedSecret?: string;
};

export type SetPasswordPayload = {
  newPassword: string;
  confirmNewPassword: string;
  verificationToken: string;
};

type SignInError = {
  error: null | string;
  error_description: string;
  error_uri: null;
};

type MFADetails = {
  bEnabled: boolean;
  enabledMfaChallengeTypes: MFATypes[];
  mfaToken: string;
};

type SignInOkay = {
  details: {
    accessToken: string;
    expiresIn: string;
    refreshToken: string;
    refreshTokenExpiresIn: string;
    scopes?: Scopes;
    tokenType: string;
    mfaDetails?: MFADetails;
    profileInitials?: string;
    firstName?: string;
    lastName?: string;
  };
  status: string;
};
type SignInNeedsMfa = {
  details: {
    accessToken: null;
    expiresIn: 0;
    refreshToken: null;
    refreshTokenExpiresIn: 0;
    scopes: [];
    tokenType: string;
    mfaDetails?: MFADetails;
  };
  status: string;
};

type SignInResponse = SignInOkay | SignInError | SignInNeedsMfa;

// const navigate = useNavigate();

const isResponseOkay = (r: SignInResponse): r is SignInOkay =>
  ((r as SignInOkay).status === "Success" || (r as SignInOkay).status === "1") &&
  !!(r as SignInOkay).details.accessToken;

const needsMFA = (r: SignInResponse): r is SignInNeedsMfa =>
  ((r as SignInNeedsMfa).status === "Success" || (r as SignInNeedsMfa).status === "1") &&
  !!(r as SignInNeedsMfa).details.mfaDetails?.bEnabled;

/* STATE */
type RequirementSuccessPayload = {
  verificationSteps: VerificationStep[];
  token: string;
  enabledMfaTypes: MFATypes[];
  enabledMfaChallengeTypes: MFATypes[];
  mobilePhoneNumber: string;
};
export enum MFAStep {
  "selectMfaType" = "selectMfaType",
  "confirmMfaCode" = "confirmMfaCode",
}
export type AuthStatus =
  | "signed_out"
  | "signed_in"
  | "pending"
  | "need_email_verified"
  | "needs_to_set_password"
  | "needs_mfa"
  | "needs_to_add_account_mfa"
  | "mfa_error"
  | "mfa_token_expired"
  | "mfa_pending"
  | "token_expired"
  | "verify_phone_number"
  | "verify_phone_code"
  | "verified";

export type SignedInMfaAuthStatus =
  | "mfa_error"
  | "mfa_token_expired"
  | "mfa_pending"
  | "token_expired"
  | "signed_in_mfa_verified"
  | "signed_in_mfa_pending"
  | "signed_in_mfa_error"
  | null;

type State = {
  appVisible: boolean;
  authStatus: AuthStatus;
  signedInMfaAuthStatus: SignedInMfaAuthStatus;
  message: string;
  setPasswordError: string | null;
  loadingSetPassword: boolean;
  mfaSelectionLoading: boolean;
  signedInMfaSelectionLoading: boolean;
  phoneCodeLoading: boolean;
  mfaEnableLoading: boolean;
  mfaEnableError: string | null | undefined;
  mfaDisableError: string | null | undefined;
  mfaToken: string | null | undefined;
  signedInMfaToken: string | null | undefined;
  mfaChallengeLoading: boolean;
  signedInMfaChallengeLoading: boolean;
  signInChallengeType: string | null | undefined;
  signedInMfaChallengeType: string | null | undefined;
  mfaChallengeError: string | null | undefined;
  phoneVerifyErrorMessage: string | null | undefined;
  mfaStep: MFAStep;
  signedInMfaStep: MFAStep;
  // mfaTypes: MFATypes[];
  selectedChallengeType?: MFATypes;
  qrCode?: string;
  requirementsError?: string;
  mobilePhoneNumber?: string;
  sharedSecret?: string;
  mfaSelectionStatus: "ok" | "success" | "error";
  verificationTokenStatus:
    | "ok"
    | "resend_link_success"
    | "resend_link_error"
    | "requesting_new_token";
  mfaSignInError: string | null | undefined;
  signedInMfaError: string | null | undefined;
  timeoutPrompt: boolean;
  sessionTime: number;
  enabledMfaChallengeTypes: MFATypes[] | undefined;
  sessionRefreshFlag: number;
  privateMfaEnableLoading: boolean;
  privateMfaEnabled: { SMS: boolean; Totp: boolean; Email: boolean };
  privateMfaDisabled: { SMS: boolean; Totp: boolean; Email: boolean };
  privateMfaDisableLoading: boolean;
  signInChallengeError: boolean;
  signedInMfaChallengeError: boolean;
  signed_in_mfa_verified: boolean;
  signed_in_mfa_pending: boolean;
  signed_in_mfa_error: boolean;
  resendVerificationLinkStatus: boolean | null;
};

export type SignInMfaChallenge = {
  mfaToken: string;
  challengeType: MFATypes;
};

export type SignedInMfaChallenge = {
  // mfaToken: string;
  challengeType: MFATypes;
};

const initialState: State = {
  authStatus: "signed_out",
  signedInMfaAuthStatus: null,
  appVisible: false,
  message: "",
  mfaStep: MFAStep.selectMfaType,
  signedInMfaStep: MFAStep.selectMfaType,
  loadingSetPassword: false,
  signInChallengeType: null,
  signedInMfaChallengeType: null,
  phoneCodeLoading: false,
  mfaSelectionLoading: false,
  signedInMfaSelectionLoading: false,
  mfaEnableError: null,
  mfaDisableError: null,
  mfaChallengeError: null,
  phoneVerifyErrorMessage: null,
  mfaEnableLoading: false,
  setPasswordError: null,
  requirementsError: "",
  mfaToken: "",
  signedInMfaToken: "",
  mfaChallengeLoading: false,
  signedInMfaChallengeLoading: false,
  mobilePhoneNumber: "",
  verificationTokenStatus: "ok",
  mfaSelectionStatus: "ok",
  sharedSecret: "",
  selectedChallengeType: undefined,
  qrCode: "",
  // mfaTypes: [],
  mfaSignInError: null,
  signedInMfaError: null,
  timeoutPrompt: false,
  sessionTime: 0,
  enabledMfaChallengeTypes: undefined,
  sessionRefreshFlag: 0,
  privateMfaEnableLoading: false,
  privateMfaEnabled: { SMS: false, Totp: false, Email: false },
  privateMfaDisabled: { SMS: false, Totp: false, Email: false },
  privateMfaDisableLoading: false,
  signInChallengeError: false,
  signedInMfaChallengeError: false,
  signed_in_mfa_verified: false,
  signed_in_mfa_pending: false,
  signed_in_mfa_error: false,
  resendVerificationLinkStatus: null,
};

/* ACTIONS */

const INVALID_VERIFICATION_TOKEN = "app/auth/INVALID_VERIFICATION_TOKEN";
const REFRESH_VERIFICATION_TOKEN = "app/auth/REFRESH_VERIFICATION_TOKEN";
const REFRESH_VERIFICATION_TOKEN_ERROR = "app/auth/REFRESH_VERIFICATION_TOKEN_ERROR";
const ACCOUNT_VERIFIED = "app/auth/ACCOUNT_VERIFIED";
const SEND_TO_LOGIN = "app/auth/SEND_TO_LOGIN";
const SET_UP_MFA = "app/auth/SET_UP_MFA";
const MFA_SIGN_IN = "app/auth/MFA_SIGN_IN";
const ATTEMPT_SIGNED_IN_MFA_AUTHENTICATION = "app/auth/ATTEMPT_SIGNED_IN_MFA_AUTHENTICATION";
const MFA_CHALLENGE_READY = "app/auth/MFA_CHALLENGE_READY";
const SIGNED_IN_MFA_CHALLENGE_READY = "app/auth/SIGNED_IN_MFA_CHALLENGE_READY";
const MFA_REQUEST_PHONE_CODE = "app/auth/MFA_REQUEST_PHONE_CODE";
const UPDATE_PHONE_NUMBER = "app/auth/UPDATE_PHONE_NUMBER";
const MFA_VERIFY_PHONE_NUMBER = "app/auth/MFA_VERIFY_PHONE_NUMBER";
const MFA_VERIFY_PHONE_CODE = "app/auth/MFA_VERIFY_PHONE_CODE";
const MFA_SUBMIT_PHONE_CODE = "app/auth/MFA_SUBMIT_PHONE_CODE";
const REQUIREMENTS = "app/auth/REQUIREMENTS";
const REQUIREMENTS_SUCCESS = "app/auth/REQUIREMENTS_SUCCESS";
const REQUIREMENTS_ERROR = "app/auth/REQUIREMENTS_ERROR";
const RESEND_LINK_SUCCESS = "app/auth/RESEND_LINK_SUCCESS";
const REQUIREMENT_VERIFY_EMAIL = "app/auth/REQUIREMENT_VERIFY_EMAIL";
const REQUIREMENT_SET_PASSWORD = "app/auth/REQUIREMENT_SET_PASSWORD";
const SET_PASSWORD = "app/auth/SET_PASSWORD";
const SET_PASSWORD_SUCCESS = "app/auth/SET_PASSWORD_SUCCESS";
const SET_PASSWORD_ERROR = "app/auth/SET_PASSWORD_ERROR";
const MFA_ENABLE = "app/auth/MFA_ENABLE";
const MFA_ENABLE_ERROR = "app/auth/MFA_ENABLE_ERROR";
const MFA_DISABLE_ERROR = "app/auth/MFA_DISABLE_ERROR";
const MFA_CHALLENGE_ERROR = "app/auth/MFA_CHALLENGE_ERROR";
const PHONE_VERIFY_ERROR = "app/auth/PHONE_VERIFY_ERROR";
const TOTP_REQUEST_ERROR = "app/auth/TOTP_REQUEST_ERROR";
const CLEAR_MFA_ENABLE_ERROR = "app/auth/CLEAR_MFA_ENABLE_ERROR";
const CLEAR_MFA_DISABLE_ERROR = "app/auth/CLEAR_MFA_DISABLE_ERROR";
const CHALLENGE_MFA_SELECTION = "app/auth/CHALLENGE_MFA_SELECTION";
const CHALLENGE_MFA = "app/auth/CHALLENGE_MFA";
const CHALLENGE_MFA_REQUEST_QR_CODE = "app/auth/CHALLENGE_MFA_REQUEST_QR_CODE";
const CHALLENGE_MFA_QR_CODE = "app/auth/CHALLENGE_MFA_QR_CODE";
const CHALLENGE_MFA_EMAIL_SUCCESS = "app/auth/CHALLENGE_MFA_EMAIL_SUCCESS";
const CHALLENGE_MFA_EMAIL_ERROR = "app/auth/CHALLENGE_MFA_EMAIL_ERROR";
const SIGN_OUT = "app/auth/SIGN_OUT";
const MFA_AUTO_SIGN_OUT = "app/auth/MFA_AUTO_SIGN_OUT";
const SIGN_IN = "app/auth/SIGN_IN";
const SIGN_IN_OKAY = "app/auth/SIGN_IN_OKAY";
const SIGN_IN_MFA = "app/auth/SIGN_IN_MFA";
const SIGN_IN_MFA_CHALLENGE = "app/auth/SIGN_IN_MFA_CHALLENGE";
const SIGNED_IN_MFA_CHALLENGE = "app/auth/SIGNED_IN_MFA_CHALLENGE";
const MFA_SIGN_IN_ERROR = "app/auth/MFA_SIGN_IN_ERROR";
const CLEAR_MFA_SIGN_IN_ERROR = "app/auth/CLEAR_MFA_SIGN_IN_ERROR";
const INVALID_MFA_TOKEN = "app/auth/INVALID_MFA_TOKEN";
const INVALID_SIGNED_IN_MFA_TOKEN = "app/auth/INVALID_SIGNED_IN_MFA_TOKEN";
const SIGN_IN_ERROR = "app/auth/SIGN_IN_ERROR";
const SHOW_APP = "app/auth/SHOW_APP";
const CHECK_SIGNED_IN = "app/auth/CHECK_SIGNED_IN";
const TIMEOUT_PROMPT = "app/auth/TIMEOUT_PROMPT";
const SET_SESSION_TIME = "app/auth/SET_SESSION_TIME";
const CLEAR_CHALLENGE_MFA = "app/auth/CLEAR_CHALLENGE_MFA";
const PRIVATE_MFA_ENABLE = "app/auth/PRIVATE_MFA_ENABLE";
const PRIVATE_MFA_ENABLED = "app/auth/PRIVATE_MFA_ENABLED";
const PRIVATE_MFA_DISABLE = "app/auth/PRIVATE_MFA_DISABLE";
const PRIVATE_MFA_DISABLED = "app/auth/PRIVATE_MFA_DISABLED";
const MULTIPLE_SIGN_IN_MFA_TYPES = "app/auth/MULTIPLE_SIGN_IN_MFA_TYPES";
const SIGNED_IN_MFA_SUCCESS = "app/auth/SIGNED_IN_MFA_SUCCESS";
const SIGNED_IN_MFA_PENDING = "app/auth/SIGNED_IN_MFA_PENDING";
const SIGNED_IN_MFA_ERROR = "app/auth/SIGNED_IN_MFA_ERROR";
const CLEAR_SIGNED_IN_MFA_ERROR = "app/auth/CLEAR_SIGNED_IN_MFA_ERROR";
const RESEND_VERIFICATION_LINK_STATUS = "app/auth/RESEND_VERIFICATION_LINK_STATUS";
const CLEAR_RESEND_VERIFICATION_LINK_STATUS = "app/auth/CLEAR_RESEND_VERIFICATION_LINK_STATUS";

type Action =
  | {
      type: typeof SET_UP_MFA;
      payload: {
        // mfaTypes: MFATypes[];
        enabledMfaChallengeTypes: MFATypes[];
        mobilePhoneNumber?: string;
        token: string;
      };
    }
  | {
      type: typeof REQUIREMENTS_SUCCESS;
      payload: RequirementSuccessPayload;
    }
  | {
      type: typeof MFA_ENABLE;
      payload: { mfaCode: string; verificationToken: string };
    }
  | { type: typeof REQUIREMENTS_ERROR; payload: { errorMessage: string } }
  | { type: typeof MFA_ENABLE_ERROR; payload: { errorMessage: string } }
  | { type: typeof MFA_DISABLE_ERROR; payload: { errorMessage: string } }
  | { type: typeof MFA_CHALLENGE_ERROR; payload: { errorMessage: string } }
  | { type: typeof PHONE_VERIFY_ERROR; payload: { errorMessage: string } }
  | { type: typeof TOTP_REQUEST_ERROR; payload: { errorMessage: string } }
  | { type: typeof CHALLENGE_MFA_REQUEST_QR_CODE; payload: RequirementsToken }
  | { type: typeof CHALLENGE_MFA; payload: MFAChallenge }
  | { type: typeof CHALLENGE_MFA_QR_CODE; payload: MFAChallenge }
  | { type: typeof UPDATE_PHONE_NUMBER; payload: { mobilePhoneNumber: string } }
  | { type: typeof RESEND_LINK_SUCCESS }
  | {
      type: typeof MFA_SUBMIT_PHONE_CODE;
      payload: { verificationToken: string; smsCode: string };
    }
  | { type: typeof CLEAR_MFA_ENABLE_ERROR }
  | { type: typeof CLEAR_MFA_DISABLE_ERROR }
  | { type: typeof MFA_REQUEST_PHONE_CODE; payload: RequirementsToken }
  | { type: typeof MFA_VERIFY_PHONE_NUMBER }
  | { type: typeof MFA_VERIFY_PHONE_CODE }
  | { type: typeof MFA_CHALLENGE_READY }
  | {
      type: typeof SIGNED_IN_MFA_CHALLENGE_READY;
      payload: { mfaToken: string };
    }
  | { type: typeof CHALLENGE_MFA_SELECTION; payload: MFAChallenge }
  | { type: typeof CHALLENGE_MFA_EMAIL_SUCCESS }
  | { type: typeof CHALLENGE_MFA_EMAIL_ERROR }
  | { type: typeof ACCOUNT_VERIFIED }
  | { type: typeof SEND_TO_LOGIN }
  | { type: typeof INVALID_VERIFICATION_TOKEN }
  | { type: typeof REFRESH_VERIFICATION_TOKEN_ERROR }
  | {
      type: typeof SET_PASSWORD_SUCCESS;
      payload: {
        verificationToken: string;
        verificationSteps: VerificationStep[];
      };
    }
  | { type: typeof REFRESH_VERIFICATION_TOKEN; payload: RequirementsToken }
  | { type: typeof SET_PASSWORD; payload: SetPasswordPayload }
  | { type: typeof SET_PASSWORD_ERROR; payload: { message: string } }
  | { type: typeof REQUIREMENTS; payload: RequirementsToken }
  | { type: typeof REQUIREMENT_SET_PASSWORD; payload: RequirementsToken }
  | { type: typeof REQUIREMENT_VERIFY_EMAIL; payload: RequirementsToken }
  | { type: typeof SIGN_IN; payload: SignInAttempt }
  | {
      type: typeof MFA_SIGN_IN;
      payload: {
        challengeType: string;
        mfaCode: string;
      };
    }
  | {
      type: typeof ATTEMPT_SIGNED_IN_MFA_AUTHENTICATION;
      payload: {
        challengeType: string;
        mfaCode: string;
      };
    }
  | { type: typeof MFA_SIGN_IN_ERROR; payload: { errorMessage: string } }
  | { type: typeof SIGNED_IN_MFA_ERROR; payload: { errorMessage: string } }
  | { type: typeof CLEAR_MFA_SIGN_IN_ERROR }
  | { type: typeof INVALID_MFA_TOKEN; payload: { errorMessage: string } }
  | {
      type: typeof INVALID_SIGNED_IN_MFA_TOKEN;
      payload: { errorMessage: string };
    }
  | { type: typeof SIGN_OUT; message: string; signOutMarker: boolean }
  | { type: typeof MFA_AUTO_SIGN_OUT; signOutMarker: boolean }
  | { type: typeof CHECK_SIGNED_IN }
  | { type: typeof SHOW_APP; payload: boolean }
  | { type: typeof SIGN_IN_MFA; payload: SignInNeedsMfa }
  | {
      type: typeof SIGN_IN_MFA_CHALLENGE;
      payload: SignInMfaChallenge;
    }
  | {
      type: typeof SIGNED_IN_MFA_CHALLENGE;
      payload: SignedInMfaChallenge;
    }
  | { type: typeof SIGN_IN_OKAY; payload: SignInOkay }
  | { type: typeof SIGN_IN_ERROR; payload: SignInError }
  | { type: typeof TIMEOUT_PROMPT; payload: boolean }
  | { type: typeof SET_SESSION_TIME; payload: number }
  | { type: typeof CLEAR_CHALLENGE_MFA; payload: {} }
  | {
      type: typeof PRIVATE_MFA_ENABLE;
      payload: { mfaCode: string; mfaToken: string; challengeType: string };
    }
  | {
      type: typeof PRIVATE_MFA_DISABLE;
      payload: { mfaCode: string; mfaToken: string; challengeType: string };
    }
  | {
      type: typeof PRIVATE_MFA_ENABLED;
      payload: { challengeType: string };
    }
  | {
      type: typeof PRIVATE_MFA_DISABLED;
      payload: { challengeType: string };
    }
  | {
      type: typeof MULTIPLE_SIGN_IN_MFA_TYPES;
    }
  | { type: typeof SIGNED_IN_MFA_SUCCESS }
  | { type: typeof SIGNED_IN_MFA_PENDING; payload: { pending: boolean } }
  | { type: typeof CLEAR_SIGNED_IN_MFA_ERROR }
  | {
      type: typeof RESEND_VERIFICATION_LINK_STATUS;
      payload: boolean | null;
    }
  | {
      type: typeof CLEAR_RESEND_VERIFICATION_LINK_STATUS;
      payload: null;
    };

/* REDUCER */

export default function reducer(state = initialState, action: Action): State {
  return produce(state, (draft) => {
    switch (action.type) {
      case TOTP_REQUEST_ERROR:
        break;
      case REQUIREMENTS_ERROR:
        draft.requirementsError = action.payload.errorMessage;
        break;
      case REQUIREMENTS_SUCCESS:
        const {
          // enabledMfaTypes,
          enabledMfaChallengeTypes,
          mobilePhoneNumber,
        } = action.payload;
        // draft.mfaTypes = enabledMfaTypes;
        draft.enabledMfaChallengeTypes = enabledMfaChallengeTypes;
        draft.mobilePhoneNumber = mobilePhoneNumber;
        break;
      case MFA_SUBMIT_PHONE_CODE:
        draft.phoneCodeLoading = true;
        break;
      case MFA_REQUEST_PHONE_CODE:
        draft.phoneCodeLoading = true;
        break;
      case REQUIREMENT_VERIFY_EMAIL:
        draft.authStatus = "need_email_verified";
        break;
      case MFA_VERIFY_PHONE_NUMBER:
        draft.authStatus = "verify_phone_number";
        break;
      case MFA_VERIFY_PHONE_CODE:
        draft.authStatus = "verify_phone_code";
        draft.phoneCodeLoading = false;
        break;
      case MFA_CHALLENGE_READY:
        draft.mfaSelectionLoading = false;
        draft.mfaChallengeLoading = false;
        draft.mfaStep = MFAStep.confirmMfaCode;
        draft.signInChallengeError = false;
        break;
      case SIGNED_IN_MFA_CHALLENGE_READY:
        draft.signedInMfaSelectionLoading = false;
        draft.signedInMfaChallengeLoading = false;
        draft.signedInMfaToken = action.payload.mfaToken;
        draft.signedInMfaStep = MFAStep.confirmMfaCode;
        draft.signedInMfaChallengeError = false;
        break;
      case PHONE_VERIFY_ERROR:
        draft.phoneVerifyErrorMessage = action.payload.errorMessage;
        draft.phoneCodeLoading = false;
        break;
      case CLEAR_MFA_ENABLE_ERROR:
        draft.mfaEnableError = null;
        break;
      case CLEAR_MFA_DISABLE_ERROR:
        draft.mfaDisableError = null;
        break;
      case MFA_CHALLENGE_ERROR:
        draft.mfaChallengeError = action.payload.errorMessage;
        draft.mfaEnableLoading = false;
        draft.mfaSelectionLoading = false;
        break;
      case MFA_ENABLE_ERROR:
        draft.mfaEnableError = action.payload.errorMessage;
        draft.mfaEnableLoading = false;
        break;
      case MFA_DISABLE_ERROR:
        draft.mfaDisableError = action.payload.errorMessage;
        draft.privateMfaDisableLoading = false;
        break;
      case SIGN_IN_MFA_CHALLENGE:
        draft.mfaToken = action.payload.mfaToken;
        draft.signInChallengeType = action.payload.challengeType;
        draft.mfaChallengeLoading = true;
        break;
      case SIGNED_IN_MFA_CHALLENGE:
        draft.signedInMfaChallengeType = action.payload.challengeType;
        draft.signedInMfaChallengeLoading = true;
        break;
      case MFA_ENABLE:
        draft.mfaEnableLoading = true;
        break;
      case CHALLENGE_MFA:
        if (action.payload.challengeType === MFATypes.Totp) {
          draft.qrCode = action.payload.qrCode;
          draft.sharedSecret = action.payload.sharedSecret;
        }
        draft.selectedChallengeType = action.payload.challengeType;
        break;
      case REQUIREMENT_SET_PASSWORD:
        draft.authStatus = "needs_to_set_password";
        break;
      case CHALLENGE_MFA_SELECTION:
        draft.mfaSelectionLoading = true;
        draft.mfaSelectionStatus = "ok";
        break;
      case CHALLENGE_MFA_EMAIL_ERROR:
        draft.mfaSelectionStatus = "error";
        break;
      case CHALLENGE_MFA_EMAIL_SUCCESS:
        draft.mfaSelectionStatus = "success";
        break;
      case UPDATE_PHONE_NUMBER:
        draft.mobilePhoneNumber = action.payload.mobilePhoneNumber;
        break;
      case SET_UP_MFA:
        draft.authStatus = "needs_to_add_account_mfa";
        // Only update enabledMfaChallengeTypes if there is a value returned in the payload from the latest requirements endpoint call
        if (action.payload.enabledMfaChallengeTypes) {
          draft.enabledMfaChallengeTypes = action.payload.enabledMfaChallengeTypes;
        }
        break;
      case REFRESH_VERIFICATION_TOKEN_ERROR:
        draft.verificationTokenStatus = "resend_link_error";
        break;
      case REFRESH_VERIFICATION_TOKEN:
        draft.verificationTokenStatus = "requesting_new_token";
        break;
      case RESEND_LINK_SUCCESS:
        draft.verificationTokenStatus = "resend_link_success";
        break;
      case INVALID_VERIFICATION_TOKEN:
        draft.authStatus = "token_expired";
        break;
      case SET_PASSWORD:
        draft.loadingSetPassword = true;
        break;
      case SET_PASSWORD_ERROR:
        draft.setPasswordError = action.payload.message;
        draft.loadingSetPassword = false;
        break;
      case ACCOUNT_VERIFIED:
        draft.loadingSetPassword = false;
        draft.authStatus = "verified";
        break;
      case SHOW_APP:
        draft.appVisible = true;
        // Is the user already signed in?
        if (action.payload === true) draft.authStatus = "signed_in";
        break;
      case SIGN_OUT:
        draft.authStatus = "signed_out";
        draft.message = action.message;
        break;
      case MFA_AUTO_SIGN_OUT:
        draft.authStatus = "signed_out";
        break;
      case MFA_SIGN_IN:
        draft.authStatus = "mfa_pending";
        draft.message = "";
        break;
      case ATTEMPT_SIGNED_IN_MFA_AUTHENTICATION:
        draft.signedInMfaAuthStatus = "mfa_pending";
        draft.message = "";
        break;
      case SIGN_IN:
        draft.authStatus = "pending";
        draft.message = "";
        break;
      case SIGN_IN_MFA:
        draft.authStatus = "needs_mfa";
        // draft.mfaTypes = action.payload.details.mfaDetails?.enabledMfaChallengeTypes!;
        draft.enabledMfaChallengeTypes =
          action.payload.details.mfaDetails?.enabledMfaChallengeTypes!;
        draft.mfaToken = action.payload.details.mfaDetails?.mfaToken;
        break;
      case SIGN_IN_OKAY:
        draft.authStatus = "signed_in";
        draft.mfaSignInError = null;
        break;
      case SIGN_IN_ERROR:
        draft.loadingSetPassword = false;
        draft.authStatus = "signed_out";
        draft.message = action.payload.error_description;
        draft.mfaChallengeLoading = false;
        break;
      case MFA_SIGN_IN_ERROR:
        draft.authStatus = "mfa_error";
        draft.mfaSignInError = action.payload.errorMessage;
        break;
      case SIGNED_IN_MFA_ERROR:
        draft.signedInMfaAuthStatus = "mfa_error";
        draft.signedInMfaError = action.payload.errorMessage;
        break;
      case CLEAR_MFA_SIGN_IN_ERROR:
        draft.mfaSignInError = null;
        break;
      case INVALID_MFA_TOKEN:
        draft.authStatus = "mfa_token_expired";
        draft.mfaSignInError = action.payload.errorMessage;
        break;
      case INVALID_SIGNED_IN_MFA_TOKEN:
        draft.signedInMfaAuthStatus = "mfa_token_expired";
        draft.signedInMfaError = action.payload.errorMessage;
        break;
      case TIMEOUT_PROMPT:
        draft.timeoutPrompt = action.payload;
        break;
      case SET_SESSION_TIME:
        draft.sessionTime = action.payload;
        draft.sessionRefreshFlag = state.sessionRefreshFlag + 1;
        break;
      case CLEAR_CHALLENGE_MFA:
        draft.selectedChallengeType = undefined;
        draft.mfaStep = MFAStep.selectMfaType;
        break;
      case PRIVATE_MFA_ENABLE:
        // draft.privateMfaEnableLoading = true;
        if (action.payload.challengeType) {
          if (action.payload.challengeType === "SMS") {
            draft.privateMfaEnabled = {
              ...draft.privateMfaEnabled,
              SMS: false,
            };
          }
          if (action.payload.challengeType === "Totp") {
            draft.privateMfaEnabled = {
              ...draft.privateMfaEnabled,
              Totp: false,
            };
          }
          if (action.payload.challengeType === "Email") {
            draft.privateMfaEnabled = {
              ...draft.privateMfaEnabled,
              Email: false,
            };
          }
        }

        draft.mfaEnableLoading = true;
        break;
      case PRIVATE_MFA_ENABLED:
        if (action.payload.challengeType) {
          if (action.payload.challengeType === "SMS") {
            draft.privateMfaEnabled = { ...draft.privateMfaEnabled, SMS: true };
            draft.privateMfaDisabled = {
              ...draft.privateMfaDisabled,
              SMS: false,
            };
          }
          if (action.payload.challengeType === "Totp") {
            draft.privateMfaEnabled = {
              ...draft.privateMfaEnabled,
              Totp: true,
            };
            draft.privateMfaDisabled = {
              ...draft.privateMfaDisabled,
              Totp: false,
            };
          }
          if (action.payload.challengeType === "Email") {
            draft.privateMfaEnabled = {
              ...draft.privateMfaEnabled,
              Email: true,
            };
            draft.privateMfaDisabled = {
              ...draft.privateMfaDisabled,
              Email: false,
            };
          }
        }

        draft.mfaEnableLoading = false;
        break;
      case PRIVATE_MFA_DISABLE:
        if (action.payload.challengeType) {
          if (action.payload.challengeType === "SMS") {
            draft.privateMfaDisabled = {
              ...draft.privateMfaDisabled,
              SMS: false,
            };
          }
          if (action.payload.challengeType === "Totp") {
            draft.privateMfaDisabled = {
              ...draft.privateMfaDisabled,
              Totp: false,
            };
          }
          if (action.payload.challengeType === "Email") {
            draft.privateMfaDisabled = {
              ...draft.privateMfaDisabled,
              Email: false,
            };
          }
        }

        draft.privateMfaDisableLoading = true;
        break;
      case PRIVATE_MFA_DISABLED:
        if (action.payload.challengeType) {
          if (action.payload.challengeType === "SMS") {
            draft.privateMfaDisabled = {
              ...draft.privateMfaDisabled,
              SMS: true,
            };
            draft.privateMfaEnabled = {
              ...draft.privateMfaEnabled,
              SMS: false,
            };
          }
          if (action.payload.challengeType === "Totp") {
            draft.privateMfaDisabled = {
              ...draft.privateMfaDisabled,
              Totp: true,
            };
            draft.privateMfaEnabled = {
              ...draft.privateMfaEnabled,
              Totp: false,
            };
          }
          if (action.payload.challengeType === "Email") {
            draft.privateMfaDisabled = {
              ...draft.privateMfaDisabled,
              Email: true,
            };
            draft.privateMfaEnabled = {
              ...draft.privateMfaEnabled,
              Email: false,
            };
          }
        }

        draft.privateMfaDisableLoading = false;
        break;
      case SIGNED_IN_MFA_SUCCESS:
        draft.signed_in_mfa_verified = true;
        break;
      case SIGNED_IN_MFA_PENDING:
        draft.signed_in_mfa_pending = action.payload.pending;
        break;

      case CLEAR_SIGNED_IN_MFA_ERROR:
        draft.signed_in_mfa_error = false;
        break;
      case RESEND_VERIFICATION_LINK_STATUS:
        draft.resendVerificationLinkStatus = action.payload;
        break;
      case CLEAR_RESEND_VERIFICATION_LINK_STATUS:
        draft.resendVerificationLinkStatus = action.payload;
        break;
    }
  });
}

/* EPICS */

const extractVerificationTokenFromRequestConfig = (config: AxiosRequestConfig) => {
  try {
    const verificationToken = config.data?.verificationToken;
    if (verificationToken) {
      return verificationToken;
    } else {
      const data = JSON.parse(config.data);
      return data.verificationToken;
    }
  } catch (_) {
    return null;
  }
};

function makeRequest<T>(config: AxiosRequestConfig) {
  return from(api.request<T>(config));
}

function saveResponse(r: SignInResponse) {
  if (isResponseOkay(r)) {
    const {
      accessToken,
      refreshToken,
      expiresIn,
      scopes,
      refreshTokenExpiresIn,
      profileInitials,
      firstName,
      lastName,
    } = r.details;
    const refreshTokenExpires = addSeconds(new Date(), parseInt(refreshTokenExpiresIn, 10));
    const tokenExpires = addSeconds(new Date(), parseInt(expiresIn, 10));

    saveTokenResource({
      accessToken,
      refreshToken,
      tokenExpires,
      refreshTokenExpires: refreshTokenExpires,
      scopes: scopes ?? [],
      profileInitials: profileInitials ?? "",
      firstName: firstName ?? "",
      lastName: lastName ?? "",
    });
  }
}

function checkResponse(r: SignInResponse) {
  if (isResponseOkay(r)) {
    return signInOkay(r);
  } else if (needsMFA(r)) {
    return mfaSignIn(r);
  }
  return signInError(r);
}

/**
 * Utility functions for localStorage preservation during sign out operations
 */

/**
 * Keys that should be preserved across localStorage.clear() operations
 */
const PRESERVED_STORAGE_KEYS = [
  TENANT_ID_KEY,
  AUTO_SIGNIN_DISABLED_TIME_KEY,
  DISABLE_AUTO_SIGNIN_KEY,
  AUTO_SIGNIN_ATTEMPT_IN_PROGRESS_KEY,
  REDIRECT_LOCATION_KEY,
] as const;

/**
 * Storage key prefix for AG Grid layouts that should be preserved during sign out
 */
const AG_GRID_LAYOUT_PREFIX = "agls:" as const;

/**
 * Saves specified localStorage values before clearing
 * @param keys - Array of localStorage keys to preserve
 * @returns Object with saved key-value pairs
 */
const preserveLocalStorageValues = (keys: readonly string[]) => {
  const preserved: Record<string, string | null> = {};

  // Preserve explicitly listed keys
  keys.forEach((key) => {
    preserved[key] = window?.localStorage.getItem(key) || null;
  });

  // Preserve all AG Grid layout keys (keys starting with "agls:")
  if (window?.localStorage) {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(AG_GRID_LAYOUT_PREFIX)) {
        preserved[key] = window.localStorage.getItem(key);
      }
    }
  }

  return preserved;
};

/**
 * Restores previously saved localStorage values
 * @param preserved - Object with key-value pairs to restore
 */
const restoreLocalStorageValues = (preserved: Record<string, string | null>) => {
  Object.entries(preserved).forEach(([key, value]) => {
    if (value !== null && window) {
      window.localStorage.setItem(key, value);
    }
  });
};

export const ACCOUNT_NOT_VERIFIED_ERROR_MESSAGE =
  "You have not completed your account verification. Please check your email for a link.";

const parseApiErrorMessageCode = (code: string): string => {
  switch (code) {
    case "Username_Or_Password_Incorrect":
      return "Invalid username or password";
    case "Account_Locked":
      return "Your account has been locked after too many failed attempts. Please contact an admin to unlock your account.";
    case "Account_Suppressed":
      return "Your account is currently inactive.";
    case "Entra_Login_Required":
      return "This account requires Single Sign-On authentication.";
    case "Account_Not_Verified":
      return ACCOUNT_NOT_VERIFIED_ERROR_MESSAGE; // Must modify the constant above to change this error message as this is referenced in the sign in page file to display "Resend verification link" clickable text in the error message.
    default:
      return "Oops, something went wrong. Please try again later.";
  }
};

const takeFirst = <T>(array: T[]) => array[0];
const isAxiosError = (err: unknown): err is AxiosError => (err ? (err as any).response : false);
const isVerificationTokenInvalid = (errors: AvamaeDefaultError[]) => {
  try {
    let isInvalid = false;
    errors.forEach((error) => {
      if (
        error.messageCode === "Verification_Token_Invalid" ||
        error.messageCode === "Verification_Token_Expired"
      ) {
        isInvalid = true;
      }
    });
    return isInvalid;
  } catch {
    return false;
  }
};
const isVerifyEmailAddress = (
  step: VerificationStep
): step is VerificationStep.VerifyEmailAddress => step === VerificationStep.VerifyEmailAddress;
const isSetNewPassword = (step: VerificationStep): step is VerificationStep.SetNewPassword =>
  step === VerificationStep.SetNewPassword;
const isMfaSetupRequired = (step: VerificationStep): step is VerificationStep.MfaSetup =>
  step === VerificationStep.MfaSetup;
const isVerifyPhoneNumber = (
  step: VerificationStep
): step is VerificationStep.VerifyMobilePhoneNumber =>
  step === VerificationStep.VerifyMobilePhoneNumber;

const handleVerificationSteps = (
  verificationSteps: VerificationStep[],
  token: string,
  // mfaTypes?: MFATypes[],
  enabledMfaChallengeTypes?: MFATypes[],
  mobilePhoneNumber?: string
) => {
  if (verificationSteps.length === 0) {
    // finished requirements
    return accountVerified();
  } else {
    const nextStep = takeFirst(verificationSteps);
    if (isVerifyEmailAddress(nextStep)) {
      // verify email
      return requirementVerifyEmail(token);
    }
    if (isSetNewPassword(nextStep)) {
      return requirementSetPassword(token);
    }
    if (isMfaSetupRequired(nextStep)) {
      //set up mfa
      return setUpMfa(
        // mfaTypes!,
        enabledMfaChallengeTypes!,
        token,
        mobilePhoneNumber
      );
    }

    if (isVerifyPhoneNumber(nextStep)) {
      // verify phone
      return verifyPhoneNumber();
    }

    return sendToLogin();
  }
};

/**
 * Requirements epic
 * - make request with token from query parameter to requirements endpoint and get the next steps for completing account verification
 */
const requirementsSuccessEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(REQUIREMENTS_SUCCESS),
    pluck<any, RequirementSuccessPayload>("payload"),
    map((payload) =>
      handleVerificationSteps(
        payload.verificationSteps,
        payload.token,
        // payload.enabledMfaTypes,
        payload.enabledMfaChallengeTypes,
        payload.mobilePhoneNumber
      )
    )
  );

const handleRequirementsError = (error: AxiosError | unknown) => {
  if (isAxiosError(error)) {
    const errors = error.response?.data?.errors as BaseAvamaeResponse["errors"];
    const token = error.response?.config.params.verificationToken;
    if (!token) {
      return of(sendToLogin());
    }
    if (!errors || errors.length === 0) {
      return of(requirementsError(UNKOWN_ERROR_MESSAGE));
    }

    if (isVerificationTokenInvalid(errors)) {
      return of(invalidVerificationToken());
    }

    const firstError = takeFirst(errors);
    let message = "";
    switch (firstError.messageCode.toLowerCase()) {
      case "account_suppressed":
        message = "Your account has been disabled. Please contact support.";
        return of(requirementsError(message));
      default:
        return of(requirementsError(UNKOWN_ERROR_MESSAGE));
    }
  } else {
    return of(requirementsError(UNKOWN_ERROR_MESSAGE));
  }
};

const requirementsEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(REQUIREMENTS),
    switchMap(({ payload }) =>
      makeRequest<RequirementsResponse>({
        url: endpoints.accountVerification.requirements,
        params: { verificationToken: payload.token },
        method: "GET",
      }).pipe(
        map((response) => {
          const enabledMfaTypes = response.data.details.enabledMfaChallengeTypes;

          const enabledMfaChallengeTypes = response.data.details.enabledMfaChallengeTypes;
          // const enabledMfaTypes =
          //   response.data.details.enabledMfaChallengeTypes;
          const mobilePhoneNumber = response.data.details.mobilePhoneNumber;
          const token = response.config.params.verificationToken;
          const verificationSteps = response.data.details.verificationRequirements;
          return requirementsSuccess({
            verificationSteps,
            token,
            enabledMfaTypes,
            enabledMfaChallengeTypes,
            mobilePhoneNumber,
          });
        }),
        catchError(handleRequirementsError)
      )
    )
  );

/**
 * Handling Setting Up MFA
 */

const handleMfaChallengeSelection = (payload: MFAChallenge) => {
  switch (payload.challengeType) {
    case MFATypes.Totp:
      return mfaChallengeRequestTotpQrCode(payload.verificationToken);
    case MFATypes.Email:
      return mfaChallenge(payload);
    case MFATypes.SMS:
      return mfaChallenge(payload);
  }
};

const mfaChallengeSelectionEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(CHALLENGE_MFA_SELECTION),
    pluck<any, MFAChallenge>("payload"),
    map(handleMfaChallengeSelection)
  );

const handleMfaEnableError = (error: AxiosError | unknown) => {
  if (isAxiosError(error)) {
    const errors = error.response?.data?.errors as BaseAvamaeResponse["errors"];
    if (!errors || errors.length === 0) {
      return of(mfaEnableError(UNKOWN_ERROR_MESSAGE));
    }
    const firstError = takeFirst(errors);
    let message = "";
    switch (firstError.messageCode.toLowerCase()) {
      case "verification_token_expired":
      case "verification_token_invalid":
        return of(invalidVerificationToken());
      case "mfa_challenge_type_invalid":
      case "mfa_challenge_type_not_allowed":
        message = "This challenge type is currently disabled, please use a different method.";
        return of(mfaEnableError(message));
      case "mfa_code_invalid":
        message =
          "The code you entered was invalid. Please check you have entered the correct code and try again.";
        return of(mfaEnableError(message));
      case "mfa_challenge_not_initiated":
      case "requirement_already_complete":
        return of(mfaEnableError(UNKOWN_ERROR_MESSAGE));
      default:
        return of(mfaEnableError(UNKOWN_ERROR_MESSAGE));
    }
  } else {
    return of(mfaEnableError(UNKOWN_ERROR_MESSAGE));
  }
};

const handleMfaDisableError = (error: AxiosError | unknown) => {
  if (isAxiosError(error)) {
    const errors = error.response?.data?.errors as BaseAvamaeResponse["errors"];
    if (!errors || errors.length === 0) {
      return of(mfaEnableError(UNKOWN_ERROR_MESSAGE));
    }
    const firstError = takeFirst(errors);

    let message = "";
    switch (firstError.messageCode.toLowerCase()) {
      case "verification_token_expired":
      case "verification_token_invalid":
        return of(invalidVerificationToken());
      case "mfa_challenge_type_invalid":
      case "mfa_challenge_type_not_allowed":
        message = "This challenge type is currently disabled, please use a different method.";
        return of(mfaDisableError(message));
      case "mfa_code_invalid":
        message =
          "The code you entered was invalid. Please check you have entered the correct code and try again.";
        return of(mfaDisableError(message));
      case "mfa_challenge_not_initiated":
      case "requirement_already_complete":
        return of(mfaDisableError(UNKOWN_ERROR_MESSAGE));
      case "mfa_mandatory":
        return of(mfaDisableError("Sorry, at least 1 MFA type must be enabled."));
      default:
        return of(mfaDisableError(UNKOWN_ERROR_MESSAGE));
    }
  } else {
    return of(mfaDisableError(UNKOWN_ERROR_MESSAGE));
  }
};

const mfaEnableEpic = (
  action$: ActionsObservable<any>,
  state$: StateObservable<any>
): Observable<Action> =>
  action$.pipe(
    ofType(MFA_ENABLE),
    pluck("payload"),
    switchMap((payload) =>
      makeRequest<MFAEnableResponse>({
        url: endpoints.accountVerification.mfaEnable,
        data: {
          verificationToken: payload.verificationToken,
          challengeType: state$.value.auth.selectedChallengeType,
          mfaCode: payload.mfaCode,
        },
        method: "POST",
      }).pipe(
        map((response) => {
          const success = response.data.status === "1";
          if (success) {
            const isVerified = response.data.details.bAccountVerified;
            if (isVerified) {
              return accountVerified();
            } else {
              const verificationSteps = response.data.details.verificationRequirements;
              const verificationToken = extractVerificationTokenFromRequestConfig(response.config);

              return handleVerificationSteps(verificationSteps, verificationToken);
            }
          } else {
            throw new Error("Unable to challenge MFA");
          }
        }),
        catchError(handleMfaEnableError)
      )
    )
  );

const handleMfaChallengeError = (error: AxiosError | unknown) => {
  if (isAxiosError(error)) {
    const errors = error.response?.data?.errors as BaseAvamaeResponse["errors"];
    if (!errors || errors.length === 0) {
      return of(mfaChallengeError(UNKOWN_ERROR_MESSAGE));
    }
    const firstError = takeFirst(errors);
    let message = "";
    switch (firstError.messageCode.toLowerCase()) {
      case "verification_token_expired":
      case "verification_token_invalid":
        return of(invalidVerificationToken());
      case "mfa_challenge_type_invalid":
      case "mfa_challenge_type_not_allowed":
        message = "This challenge type is currently disabled, please use a different method.";
        return of(mfaChallengeError(message));
      case "qr_code_not_requested":
        message = "Please set up your authenticator before completing this step.";
        return of(mfaChallengeError(message));
      default:
        return of(mfaChallengeError(UNKOWN_ERROR_MESSAGE));
    }
  } else {
    return of(mfaChallengeError(UNKOWN_ERROR_MESSAGE));
  }
};

const mfaChallengeEpic = (
  action$: ActionsObservable<any>,
  state$: StateObservable<any>
): Observable<Action> =>
  action$.pipe(
    ofType(CHALLENGE_MFA),
    pluck<any, MFAChallenge>("payload"),
    switchMap((payload) =>
      makeRequest<MFAChallengeResponse>({
        url: endpoints.accountVerification.mfaChallenge,
        data: {
          verificationToken: payload.verificationToken,
          challengeType: payload.challengeType,
          ...(payload.challengeType === MFATypes.SMS
            ? { phoneNumber: state$.value.auth.mobilePhoneNumber }
            : {}),
        },
        method: "POST",
      }).pipe(
        map((response) => {
          const success = response.data.status === "1";
          if (success) {
            return mfaChallengeReady();
          } else {
            throw new Error("Unable to challenge MFA");
          }
        }),
        catchError(handleMfaChallengeError)
      )
    )
  );

const handlePhoneCodeError = (error: AxiosError | unknown) => {
  if (isAxiosError(error)) {
    const errors = error.response?.data?.errors as BaseAvamaeResponse["errors"];
    if (!errors || errors.length === 0) {
      return of(mfaEnableError(UNKOWN_ERROR_MESSAGE));
    }
    const firstError = takeFirst(errors);
    let message = "";
    switch (firstError.messageCode.toLowerCase()) {
      case "verification_token_expired":
      case "verification_token_invalid":
        return of(invalidVerificationToken());
      case "verification_code_invalid":
      case "sms_code_invalid":
        message = "The code you entered is invalid. Please request a new code and try again.";
        return of(phoneVerifyError(message));
      case "requirement_already_complete":
        return of(phoneVerifyError(UNKOWN_ERROR_MESSAGE));
      default:
        return of(phoneVerifyError(UNKOWN_ERROR_MESSAGE));
    }
  } else {
    return of(phoneVerifyError(UNKOWN_ERROR_MESSAGE));
  }
};

const phoneSubmitCodeEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(MFA_SUBMIT_PHONE_CODE),
    pluck<any, { verificationToken: string; smsCode: string }>("payload"),
    switchMap((payload) =>
      makeRequest<BaseAvamaeResponse>({
        url: endpoints.accountVerification.mobilePhoneSubmitCode,
        data: {
          verificationCode: payload.smsCode,
          verificationToken: payload.verificationToken,
        },
        method: "POST",
      }).pipe(
        map((response) => {
          const success = response.data.status === "1";
          if (success) {
            const isVerified = response.data.details.bAccountVerified;

            if (isVerified) {
              return accountVerified();
            } else {
              const verificationSteps = response.data.details.verificationRequirements;
              const verificationToken = extractVerificationTokenFromRequestConfig(response.config);

              return handleVerificationSteps(verificationSteps, verificationToken);
            }
          } else {
            throw new Error("Unable to challenge MFA");
          }
        }),
        catchError(handlePhoneCodeError)
      )
    )
  );

const phoneRequestCodeEpic = (
  action$: ActionsObservable<any>,
  state$: StateObservable<any>
): Observable<Action> =>
  action$.pipe(
    ofType(MFA_REQUEST_PHONE_CODE),
    pluck<any, RequirementsToken>("payload"),
    switchMap((payload) =>
      makeRequest<BaseAvamaeResponse>({
        url: endpoints.accountVerification.mobilePhoneRequestCode,
        data: {
          verificationToken: payload.token,
          mobilePhoneNumber: state$.value.auth.mobilePhoneNumber,
        },
        method: "POST",
      }).pipe(
        map((response) => {
          const success = response.data.status === "1";
          if (success) {
            return mobilePhoneVerifyReady();
          } else {
            throw new Error("Unable to challenge MFA");
          }
        }),
        catchError(handlePhoneCodeError)
      )
    )
  );

const handleTotpRequestError = (error: AxiosError | unknown) => {
  if (isAxiosError(error)) {
    const errors = error.response?.data?.errors as BaseAvamaeResponse["errors"];
    if (!errors || errors.length === 0) {
      return of(mfaChallengeError(UNKOWN_ERROR_MESSAGE));
    }
    const firstError = takeFirst(errors);
    let message = "";
    switch (firstError.messageCode.toLowerCase()) {
      case "verification_token_expired":
      case "verification_token_invalid":
        return of(invalidVerificationToken());
      case "requirement_already_complete":
        message = "This step has already been completed. Please refresh the page and try again.";
        return of(mfaChallengeError(message));
      default:
        return of(mfaChallengeError(UNKOWN_ERROR_MESSAGE));
    }
  } else {
    return of(mfaChallengeError(UNKOWN_ERROR_MESSAGE));
  }
};

const mfaChallengeRequestTotpQrCodeEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(CHALLENGE_MFA_REQUEST_QR_CODE),
    pluck<any, RequirementsToken>("payload"),
    switchMap((payload) =>
      makeRequest<any>({
        url: endpoints.accountVerification.requestTotpQrCode,
        data: { verificationToken: payload.token },
        method: "POST",
      }).pipe(
        map((response) => {
          const verificationToken = extractVerificationTokenFromRequestConfig(response.config);
          if (!verificationToken) {
            throw new Error("No verification token in response config data");
          }
          const success = response.data.status === "1";
          if (success) {
            const qrCode = response.data.details.qrCode;
            const sharedSecret = response.data.details.sharedSecret;
            return mfaChallenge({
              challengeType: MFATypes.Totp,
              verificationToken,
              qrCode,
              sharedSecret,
            });
          } else {
            throw new Error();
          }
        }),
        catchError(handleTotpRequestError)
      )
    )
  );

const privateMfaEnableEpic = (
  action$: ActionsObservable<any>,
  state$: StateObservable<any>
): Observable<Action> =>
  action$.pipe(
    ofType(PRIVATE_MFA_ENABLE),
    pluck("payload"),
    switchMap((payload) =>
      makeRequest<MFAEnableResponse>({
        url: endpoints.settings.enableChallengeType,
        data: {
          mfaToken: payload.mfaToken,
          challengeType: payload.challengeType,
          mfaCode: payload.mfaCode,
        },
        method: "POST",
      }).pipe(
        map((response) => {
          const success = response.data.status === "1";
          if (success) {
            return privateMfaEnabled({ challengeType: payload.challengeType });
          } else {
            throw new Error("Unable to enable MFA");
          }
        }),
        catchError(handleMfaEnableError)
      )
    )
  );

const privateMfaDisableEpic = (
  action$: ActionsObservable<any>,
  state$: StateObservable<any>
): Observable<Action> =>
  action$.pipe(
    ofType(PRIVATE_MFA_DISABLE),
    pluck("payload"),
    switchMap((payload) =>
      makeRequest<MFADisableResponse>({
        url: endpoints.settings.disableChallengeType,
        data: {
          mfaToken: payload.mfaToken,
          challengeType: payload.challengeType,
          mfaCode: payload.mfaCode,
        },
        method: "POST",
      }).pipe(
        map((response) => {
          const success = response.data.status === "1";
          if (success) {
            return privateMfaDisabled({ challengeType: payload.challengeType });
          } else {
            throw new Error("Unable to disable MFA");
          }
        }),
        catchError(handleMfaDisableError)
      )
    )
  );

const setPasswordSuccessEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(SET_PASSWORD_SUCCESS),
    pluck("payload"),
    map((payload) => handleVerificationSteps(payload.verificationSteps, payload.verificationToken))
  );

const handleSetPasswordError = (error: AxiosError | unknown) => {
  if (isAxiosError(error)) {
    const errors = error.response?.data?.errors as BaseAvamaeResponse["errors"];
    if (!errors || errors.length === 0) {
      return of(setPasswordError(UNKOWN_ERROR_MESSAGE));
    }
    const firstError = takeFirst(errors);
    let message = "";

    console.log("firstError.messageCode.toLowerCase(): ", firstError.messageCode.toLowerCase());
    switch (firstError.messageCode.toLowerCase()) {
      case "verification_token_expired":
      case "verification_token_invalid":
        console.log("0: ");
        return of(invalidVerificationToken());
      case "requirement_already_complete":
        message = "This step has already been completed. Please refresh the page.";
        return of(setPasswordError(message));
      case "invalid_password_length":
        message = "Password must be between 8 and 20 characters";
        return of(setPasswordError(message));
      case "password_no_match":
        message = "Password and confirm password must match.";
        return of(setPasswordError(message));
      case "password_policy_no_match":
        message =
          "Password must contain two of either an uppercase character, numeric or a special character";
        return of(setPasswordError(message));
      case "password_previously_used":
        message = "Password must not be a previously used password";
        return of(setPasswordError(message));
      case "password_cannot_be_username":
        console.log("1: ");
        message = "Password must not be your username";

        console.log("message: ", message);
        return of(setPasswordError(message));
      default:
        console.log(
          "firstError.messageCode.toLowerCase() === 'password_cannot_be_username': ",
          firstError.messageCode.toLowerCase() === "password_cannot_be_username"
        );
        console.log("default: ");
        return of(setPasswordError(UNKOWN_ERROR_MESSAGE));
    }
  } else {
    return of(setPasswordError(UNKOWN_ERROR_MESSAGE));
  }
};

const setPasswordEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(SET_PASSWORD),
    pluck("payload"),
    switchMap((payload) =>
      makeRequest<SetPasswordResponse>({
        url: endpoints.accountVerification.setPassword,
        data: payload,
        method: "POST",
      }).pipe(
        map((response) => {
          const isVerified = response.data.details.bAccountVerified;
          const verificationSteps = response.data.details.verificationRequirements;
          const verificationToken = extractVerificationTokenFromRequestConfig(response.config);

          if (isVerified) {
            return accountVerified();
          } else {
            return setPasswordSuccess({ verificationSteps, verificationToken });
          }
        }),
        catchError(handleSetPasswordError)
      )
    )
  );

const verifyEmailRequirementEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(REQUIREMENT_VERIFY_EMAIL),
    switchMap(({ payload }) =>
      makeRequest<VerifyEmailResponse>({
        url: endpoints.accountVerification.verifyEmail,
        data: { verificationToken: payload.token },
        method: "POST",
      }).pipe(
        map((response) => {
          const isVerified = response.data.details.bAccountVerified;
          const verificationToken = extractVerificationTokenFromRequestConfig(response.config);
          const verificationSteps = response.data.details.verificationRequirements;

          if (isVerified) {
            return accountVerified();
          } else {
            return handleVerificationSteps(verificationSteps, verificationToken);
          }
        }),
        catchError(() => {
          return of(
            requirementsError(
              "Sorry, we couldn't verify your email address, please try again later. If the problem persists, please contact support."
            )
          );
        })
      )
    )
  );

const sendToLoginEpic = (action$: ActionsObservable<any>): Observable<Action> => {
  return action$.pipe(
    ofType(SEND_TO_LOGIN),
    tap(() => {
      window.location.href = APP_ROUTES.signIn;
    }),
    ignoreElements()
  );
};

const refreshVerificationTokenEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(REFRESH_VERIFICATION_TOKEN),
    pluck("payload"),
    switchMap((payload) =>
      makeRequest({
        url: endpoints.accountVerification.resendLink,
        data: { verificationToken: payload.token },
        method: "POST",
      }).pipe(
        map(verificationLinkSent),
        catchError(() => {
          return of(verificationTokenRefreshError());
        })
      )
    )
  );

const handleMfaSignInError = (error: AxiosError | unknown) => {
  if (isAxiosError(error)) {
    const errors = error.response?.data?.errors as BaseAvamaeResponse["errors"];
    if (!errors || errors.length === 0) {
      return of(mfaEnableError(UNKOWN_ERROR_MESSAGE));
    }
    const firstError = takeFirst(errors);
    let message = "";
    switch (firstError.messageCode.toLowerCase()) {
      case "mfa_token_expired":
        message = "Session expired. Please try again.";
        return of(invalidMfaToken(message));
      case "mfa_token_invalid":
        message =
          "Sorry, something went wrong. Please try again. If the problem persists, please contact support.";
        return of(invalidMfaToken(message));
      case "mfa_challenge_type_invalid":
        message = "This challenge type is currently disabled, please use a different method.";
        return of(mfaSigninError(message));
      case "mfa_code_invalid":
        message =
          "The code you entered was invalid. Please check you have entered the correct code and try again.";
        return of(mfaSigninError(message));
      case "account_locked":
        message =
          "Your account has been locked after too many failed attempts. Please contact an admin to unlock your account.";
        return of(mfaSigninError(message));
      case "account_suppressed":
        message = "Your account is currently inactive.";
        return of(mfaSigninError(message));
      default:
        return of(mfaSigninError(UNKOWN_ERROR_MESSAGE));
    }
  } else {
    return of(mfaSigninError(UNKOWN_ERROR_MESSAGE));
  }
};

const handleSignedInMfaError = (error: AxiosError | unknown) => {
  if (isAxiosError(error)) {
    const errors = error.response?.data?.errors as BaseAvamaeResponse["errors"];
    if (!errors || errors.length === 0) {
      return of(signedInMfaError(UNKOWN_ERROR_MESSAGE));
    }
    const firstError = takeFirst(errors);
    let message = "";
    switch (firstError.messageCode.toLowerCase()) {
      case "mfa_token_expired":
        message = "Session expired. Please try again.";
        return of(invalidSignedInMfaToken(message));
      case "mfa_token_invalid":
        message =
          "Sorry, something went wrong. Please try again. If the problem persists, please contact support.";
        return of(invalidSignedInMfaToken(message));
      case "mfa_challenge_type_invalid":
        message = "This challenge type is currently disabled, please use a different method.";
        return of(signedInMfaError(message));
      case "mfa_code_invalid":
        message =
          "The code you entered was invalid. Please check you have entered the correct code and try again.";
        return of(signedInMfaError(message));
      case "account_locked":
        message =
          "Your account has been locked after too many failed attempts. Please contact an admin to unlock your account.";
        return of(signedInMfaError(message));
      case "account_suppressed":
        message = "Your account is currently inactive.";
        return of(signedInMfaError(message));
      default:
        return of(signedInMfaError(UNKOWN_ERROR_MESSAGE));
    }
  } else {
    return of(signedInMfaError(UNKOWN_ERROR_MESSAGE));
  }
};

const mfaSignInEpic = (
  action$: ActionsObservable<any>,
  state$: StateObservable<any>
): Observable<Action> =>
  action$.pipe(
    ofType(MFA_SIGN_IN),
    switchMap((attempt) =>
      makeRequest<SignInResponse>({
        url: endpoints.auth.mfaLogin,
        data: {
          ...attempt.payload,
          mfaToken: state$.value.auth.mfaToken,
          host: HOST,
        },
        method: "post",
      }).pipe(
        tap((res) => {
          saveResponse(res.data);

          clearMfaAutoSignOut(); // Clear MFA auto sign out localstorage flag
        }),
        map((res) => checkResponse(res.data)),
        catchError(handleMfaSignInError)
      )
    )
  );

const signInMfaChallengeEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(SIGN_IN_MFA_CHALLENGE),
    pluck<any, SignInMfaChallenge>("payload"),
    switchMap((payload) =>
      makeRequest<any>({
        url: endpoints.auth.mfaChallenge,
        data: payload,
        method: "POST",
        // method: "GET",
      }).pipe(
        map((response) => {
          const success = response.data.status === "1";
          if (success) {
            return mfaChallengeReady();
          } else {
            handleMfaSignInError(null);
            throw new Error("Unable to challenge MFA");
          }
        }),
        catchError(handleMfaSignInError)
      )
    )
  );

const signedInMfaChallengeEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(SIGNED_IN_MFA_CHALLENGE),
    pluck<any, SignInMfaChallenge>("payload"),
    switchMap((payload) =>
      makeRequest<any>({
        url: endpoints.profile.mfaChallenge,
        data: payload,
        method: "POST",
        // method: "GET",
      }).pipe(
        map((response) => {
          const success = response.data.status === "1";
          if (
            success &&
            response &&
            response.data &&
            response.data.details &&
            response.data.details.mfaToken
          ) {
            return signedInMfaChallengeReady(response.data.details.mfaToken);
          } else {
            handleSignedInMfaError(null);
            throw new Error("Unable to challenge MFA");
          }
        }),
        catchError(handleSignedInMfaError)
      )
    )
  );

const signInMfaEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(SIGN_IN_MFA),
    pluck<any, SignInNeedsMfa>("payload"),
    map((payload) => {
      const challengeTypes = payload.details.mfaDetails?.enabledMfaChallengeTypes;
      const mfaToken = payload.details.mfaDetails?.mfaToken;
      if (challengeTypes && challengeTypes.length === 1 && mfaToken) {
        return mfaSignInChallenge({
          challengeType: takeFirst(challengeTypes),
          mfaToken,
        });
      } else {
        return mfaSignInMultipleTypes();
      }
    })
  );
/** Attempt to sign in and handle response, cancel if we sign out while a request pending. */
const signInEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(SIGN_IN),
    switchMap((attempt) =>
      makeRequest<SignInResponse>({
        url: endpoints.auth.login,
        data: attempt.payload,
        method: "post",
      }).pipe(
        tap((res) => {
          // IXSTD-5436: Clear lingering signOutAlert flag on successfuly login
          if (localStorage.getItem("signOutAlert")) {
            localStorage.removeItem("signOutAlert");
          }

          // Clear AutoSignInDisabledTime flag on successful login
          if (localStorage.getItem(AUTO_SIGNIN_DISABLED_TIME_KEY)) {
            localStorage.removeItem(AUTO_SIGNIN_DISABLED_TIME_KEY);
          }

          // Clear the persistent disable auto sign-in flag on successful login
          if (localStorage.getItem(DISABLE_AUTO_SIGNIN_KEY)) {
            localStorage.removeItem(DISABLE_AUTO_SIGNIN_KEY);
          }

          saveResponse(res.data);

          clearMfaAutoSignOut(); // Clear MFA auto sign out localstorage flag
        }),
        map((res) => checkResponse(res.data)),
        catchError((err: AxiosError) => {
          const errorMessage = parseApiErrorMessageCode(
            err.response?.data.errors &&
              err.response.data.errors.length &&
              err.response.data.errors.length > 0
              ? err.response?.data.errors[0].messageCode
              : ""
          );
          return of(
            signInError({
              error: null,
              error_description: `${errorMessage}`,
              error_uri: null,
            })
          );
        })
        //takeUntil(action$.pipe(ofType(SIGN_OUT, SIGN_IN_ERROR)))
      )
    )
  );

/** Clear the local storage on sign out, so we don't appear logged in on refresh. */
const signOutEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(SIGN_OUT),
    switchMap((action) => {
      const accessToken = window?.localStorage.getItem("AT");
      const refreshToken = window?.localStorage.getItem("RT");

      const cleanup = () => {
        const preservedValues = preserveLocalStorageValues(PRESERVED_STORAGE_KEYS);
        window && window.localStorage.clear();
        restoreLocalStorageValues(preservedValues);

        if (action.signOutMarker) {
          window && window.localStorage.setItem(AUTO_SIGNIN_DISABLED_TIME_KEY, Date.now().toString());
          window && window.localStorage.setItem("signOutAlert", "true");
        }
      };

      if (action.signOutMarker && accessToken && refreshToken) {
        return from(
          api.post(endpoints.auth.logout, { accessToken, refreshToken })
        ).pipe(
          tap(() => cleanup()),
          catchError(() => {
            cleanup();
            return of(null);
          }),
          ignoreElements()
        );
      }

      cleanup();
      return of(null).pipe(ignoreElements());
    })
  );

/** Custom Sign out epic for public MFA pages, we add an custom flag to local storage so we always clear the Refresh and access tokens so that the user can't access the private pages of the site when changing the URL */
const mfaAutoSignOutEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(MFA_AUTO_SIGN_OUT),
    tap((action) => {
      // Preserve important flags before clearing localStorage
      const preservedValues = preserveLocalStorageValues(PRESERVED_STORAGE_KEYS);

      // Clear all local storage
      window && window.localStorage.clear();

      // Restore preserved values
      restoreLocalStorageValues(preservedValues);

      // Add MFA auto sign out flag to local storage
      window && window.localStorage.setItem("mfaAutoSignOut", "true");

      // Only set sign out flags for actual sign out events, not cross-tab notifications
      if (action.signOutMarker) {
        window && window.localStorage.setItem(AUTO_SIGNIN_DISABLED_TIME_KEY, Date.now().toString());
        window && window.localStorage.setItem("signOutAlert", "true");
      }
    }),
    ignoreElements()
  );

const checkSignedInEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(CHECK_SIGNED_IN),
    map(loadTokenResource),
    map((r) => r !== null),
    map(showApp)
  );

const signInOkayEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(SIGN_IN_OKAY),
    tap((action) => saveResponse(action.payload)),
    ignoreElements()
  );

export const authEpic = combineEpics(
  mfaChallengeSelectionEpic,
  mfaChallengeEpic,
  sendToLoginEpic,
  phoneRequestCodeEpic,
  requirementsSuccessEpic,
  mfaEnableEpic,
  phoneSubmitCodeEpic,
  mfaChallengeRequestTotpQrCodeEpic,
  refreshVerificationTokenEpic,
  requirementsEpic,
  setPasswordSuccessEpic,
  setPasswordEpic,
  verifyEmailRequirementEpic,
  mfaSignInEpic,
  signInMfaEpic,
  signInMfaChallengeEpic,
  signedInMfaChallengeEpic,
  signInEpic,
  signOutEpic,
  checkSignedInEpic,
  signInOkayEpic,
  mfaAutoSignOutEpic,
  privateMfaEnableEpic,
  privateMfaDisableEpic
);

/* ACTION CREATORS */

export const accountVerification = (token: string): Action => ({
  type: REQUIREMENTS,
  payload: { token },
});

export const verificationLinkSent = (): Action => ({
  type: RESEND_LINK_SUCCESS,
});

export const requirementsSuccess = (payload: {
  verificationSteps: VerificationStep[];
  token: string;
  enabledMfaTypes: MFATypes[];
  enabledMfaChallengeTypes: MFATypes[];
  mobilePhoneNumber: string;
}): Action => ({
  type: REQUIREMENTS_SUCCESS,
  payload,
});

export const requirementVerifyEmail = (token: string): Action => ({
  type: REQUIREMENT_VERIFY_EMAIL,
  payload: { token },
});

export const clearMfaEnableError = (): Action => ({
  type: CLEAR_MFA_ENABLE_ERROR,
});
export const clearMfaDisableError = (): Action => ({
  type: CLEAR_MFA_DISABLE_ERROR,
});

export const requirementSetPassword = (token: string): Action => ({
  type: REQUIREMENT_SET_PASSWORD,
  payload: { token },
});

export const setPasswordError = (message: string): Action => ({
  type: SET_PASSWORD_ERROR,
  payload: { message },
});

export const updatePhoneNumber = (mobilePhoneNumber: string): Action => ({
  type: UPDATE_PHONE_NUMBER,
  payload: { mobilePhoneNumber },
});

export const mfaEmailChallengeSuccess = (): Action => ({
  type: CHALLENGE_MFA_EMAIL_SUCCESS,
});

export const mfaChallengeSelection = (payload: MFAChallenge): Action => ({
  type: CHALLENGE_MFA_SELECTION,
  payload,
});

export const mfaChallengeRequestTotpQrCode = (token: string): Action => ({
  type: CHALLENGE_MFA_REQUEST_QR_CODE,
  payload: { token },
});

export const mfaChallenge = (payload: MFAChallenge): Action => ({
  type: CHALLENGE_MFA,
  payload,
});

export const clearMfaChallenge = (payload: {}): Action => ({
  type: CLEAR_CHALLENGE_MFA,
  payload,
});

export const mfaEnable = (mfaCode: string, verificationToken: string): Action => ({
  type: MFA_ENABLE,
  payload: { mfaCode, verificationToken },
});

export const privateMfaEnable = (
  mfaCode: string,
  mfaToken: string,
  challengeType: string
): Action => ({
  type: PRIVATE_MFA_ENABLE,
  payload: { mfaCode, mfaToken, challengeType },
});

export const privateMfaDisable = (
  mfaCode: string,
  mfaToken: string,
  challengeType: string
): Action => ({
  type: PRIVATE_MFA_DISABLE,
  payload: { mfaCode, mfaToken, challengeType },
});

export const mfaEnableError = (errorMessage: string): Action => ({
  type: MFA_ENABLE_ERROR,
  payload: { errorMessage },
});

export const mfaDisableError = (errorMessage: string): Action => ({
  type: MFA_DISABLE_ERROR,
  payload: { errorMessage },
});

export const mfaChallengeError = (errorMessage: string): Action => ({
  type: MFA_CHALLENGE_ERROR,
  payload: { errorMessage },
});
export const requirementsError = (errorMessage: string): Action => ({
  type: REQUIREMENTS_ERROR,
  payload: { errorMessage },
});

export const phoneVerifyError = (errorMessage: string): Action => ({
  type: PHONE_VERIFY_ERROR,
  payload: { errorMessage },
});
export const totpRequestError = (errorMessage: string): Action => ({
  type: TOTP_REQUEST_ERROR,
  payload: { errorMessage },
});
export const invalidVerificationToken = (): Action => ({
  type: INVALID_VERIFICATION_TOKEN,
});

export const refreshVerificationToken = (token: string): Action => ({
  type: REFRESH_VERIFICATION_TOKEN,
  payload: { token },
});
export const verificationTokenRefreshError = (): Action => ({
  type: REFRESH_VERIFICATION_TOKEN_ERROR,
});

export const sendToLogin = (): Action => ({
  type: SEND_TO_LOGIN,
});

export const mfaChallengeReady = (): Action => ({
  type: MFA_CHALLENGE_READY,
});
export const signedInMfaChallengeReady = (mfaToken: string): Action => ({
  type: SIGNED_IN_MFA_CHALLENGE_READY,
  payload: { mfaToken },
});

export const mfaPhoneRequestCode = (verificationToken: string): Action => ({
  type: MFA_REQUEST_PHONE_CODE,
  payload: { token: verificationToken },
});

export const mfaPhoneSubmitCode = ({
  smsCode,
  verificationToken,
}: {
  smsCode: string;
  verificationToken: string;
}): Action => ({
  type: MFA_SUBMIT_PHONE_CODE,
  payload: { verificationToken, smsCode },
});

export const verifyPhoneNumber = (): Action => ({
  type: MFA_VERIFY_PHONE_NUMBER,
});

export const mobilePhoneVerifyReady = (): Action => ({
  type: MFA_VERIFY_PHONE_CODE,
});

export const setUpMfa = (
  // mfaTypes: MFATypes[],
  enabledMfaChallengeTypes: MFATypes[],
  token: string,
  mobilePhoneNumber?: string
): Action => ({
  type: SET_UP_MFA,
  payload: {
    // mfaTypes,
    enabledMfaChallengeTypes,
    mobilePhoneNumber,
    token,
  },
});

export const accountVerified = (): Action => ({
  type: ACCOUNT_VERIFIED,
});

export const privateMfaEnabled = (payload: any): Action => ({
  type: PRIVATE_MFA_ENABLED,
  payload,
});

export const privateMfaDisabled = (payload: any): Action => ({
  type: PRIVATE_MFA_DISABLED,
  payload,
});

export const setPassword = (payload: any): Action => ({
  type: SET_PASSWORD,
  payload,
});

export const setPasswordSuccess = (payload: {
  verificationToken: string;
  verificationSteps: VerificationStep[];
}): Action => ({
  type: SET_PASSWORD_SUCCESS,
  payload,
});

export const signIn = (payload: SignInAttempt): Action => ({
  type: SIGN_IN,
  payload,
});

export const attemptMfaSignIn = (payload: { challengeType: string; mfaCode: string }): Action => ({
  type: MFA_SIGN_IN,
  payload,
});

export const attemptSignedInMfaAuthentication = (payload: {
  challengeType: string;
  mfaCode: string;
}): Action => ({
  type: ATTEMPT_SIGNED_IN_MFA_AUTHENTICATION,
  payload,
});

export const mfaSigninError = (errorMessage: string): Action => ({
  type: MFA_SIGN_IN_ERROR,
  payload: { errorMessage },
});
export const signedInMfaError = (errorMessage: string): Action => ({
  type: SIGNED_IN_MFA_ERROR,
  payload: { errorMessage },
});

export const clearMfaSigninError = (): Action => ({
  type: CLEAR_MFA_SIGN_IN_ERROR,
});

export const invalidMfaToken = (errorMessage: string): Action => ({
  type: INVALID_MFA_TOKEN,
  payload: { errorMessage },
});
export const invalidSignedInMfaToken = (errorMessage: string): Action => ({
  type: INVALID_SIGNED_IN_MFA_TOKEN,
  payload: { errorMessage },
});

export const signOut = (message = "", marker = true): Action => ({
  type: SIGN_OUT,
  message,
  signOutMarker: marker,
});

export const mfaAutoSignOut = (marker = true): Action => ({
  type: MFA_AUTO_SIGN_OUT,
  signOutMarker: marker,
});

export const checkSignedIn = (): Action => ({
  type: CHECK_SIGNED_IN,
});

export const signInOkay = (payload: SignInOkay): Action => ({
  type: SIGN_IN_OKAY,
  payload,
});

export const mfaSignInChallenge = (payload: SignInMfaChallenge): Action => ({
  type: SIGN_IN_MFA_CHALLENGE,
  payload,
});

export const signedInMfaChallenge = (payload: SignedInMfaChallenge): Action => ({
  type: SIGNED_IN_MFA_CHALLENGE,
  payload,
});

export const mfaSignInMultipleTypes = (): Action => ({
  type: MULTIPLE_SIGN_IN_MFA_TYPES,
});

const mfaSignIn = (payload: SignInNeedsMfa): Action => ({
  type: SIGN_IN_MFA,
  payload,
});

const signInError = (payload: SignInError): Action => ({
  type: SIGN_IN_ERROR,
  payload,
});

export { signInError };

const showApp = (payload: boolean): Action => ({
  type: SHOW_APP,
  payload,
});

export const timeoutPrompt = (payload: boolean): Action => ({
  type: TIMEOUT_PROMPT,
  payload,
});

export const sessionTime = (payload: number): Action => ({
  type: SET_SESSION_TIME,
  payload,
});

export const setResendVerificationLinkStatus = (payload: boolean | null): Action => ({
  type: RESEND_VERIFICATION_LINK_STATUS,
  payload,
});
export const clearResendVerificationLinkStatus = (): Action => ({
  type: CLEAR_RESEND_VERIFICATION_LINK_STATUS,
  payload: null,
});

/* SELECTORS */

export const selectAuthStatus = (app: Store) => app.auth.authStatus;
export const selectSignInChallengeType = (app: Store) => app.auth.signInChallengeType;
export const selectSignInMFAToken = (app: Store) => app.auth.mfaToken;
export const selectMFAChallengeLoading = (app: Store) => app.auth.mfaChallengeLoading;
export const selectRequirementsError = (app: Store) => app.auth.requirementsError;
// export const selectMFATypes = (app: Store) => app.auth.mfaTypes;
export const selectPhoneCodeDetails = (app: Store) => ({
  phoneCodeLoading: app.auth.phoneCodeLoading,
  phoneVerifyErrorMessage: app.auth.phoneVerifyErrorMessage,
});
export const selectMobilePhoneNumber = (app: Store) => app.auth.mobilePhoneNumber;
export const selectMFASelectionDetails = (app: Store) => ({
  selectedChallengeType: app.auth.selectedChallengeType,
  qrCode: app.auth.qrCode,
  sharedSecret: app.auth.sharedSecret,
  mfaEnableLoading: app.auth.mfaEnableLoading,
  mfaEnableError: app.auth.mfaEnableError,
  mfaChallengeError: app.auth.mfaChallengeError,
  mfaStep: app.auth.mfaStep,
  mfaSelectionLoading: app.auth.mfaSelectionLoading,
});
export const selectMFASignInError = (app: Store) => app.auth.mfaSignInError;
export const selectSignedIn = createSelector(selectAuthStatus, (status) => status === "signed_in");
export const selectAuthMessage = (app: Store) => app.auth.message;
export const selectTokenStatus = (app: Store) => app.auth.verificationTokenStatus;
export const selectLoadingSetPassword = (app: Store) => app.auth.loadingSetPassword;
export const selectSetPasswordError = (app: Store) => app.auth.setPasswordError;
export const selectAppVisible = (app: Store) => app.auth.appVisible;
export const selectTimeoutPrompt = (app: Store) => app.auth.timeoutPrompt;
export const selectSessionTime = (app: Store) => ({
  sessionTime: app.auth.sessionTime,
  sessionRefreshFlag: app.auth.sessionRefreshFlag,
});
export const selectEnabledMfaChallengeTypes = (app: Store) => app.auth.enabledMfaChallengeTypes;
export const selectPrivateMfaEnabled = (app: Store) => app.auth.privateMfaEnabled;

export const selectMFADisableDetails = (app: Store) => ({
  privateMfaDisableLoading: app.auth.privateMfaDisableLoading,
  privateMfaDisabled: app.auth.privateMfaDisabled,
  mfaDisableError: app.auth.mfaDisableError,
});

export const selectSignedInMfaVerified = (app: Store) => app.auth.signed_in_mfa_verified;
export const selectSignedInMfaPending = (app: Store) => app.auth.signed_in_mfa_pending;
export const selectSignedInMfaError = (app: Store) => app.auth.signed_in_mfa_error;
export const selectSignedInMfaToken = (app: Store) => app.auth.signedInMfaToken;
export const selectSignedInMfaChallengeLoading = (app: Store) =>
  app.auth.signedInMfaChallengeLoading;
export const selectSignedInMfaChallengeError = (app: Store) => app.auth.signedInMfaChallengeError;
export const selectResendVerificationLinkStatus = (app: Store) =>
  app.auth.resendVerificationLinkStatus;
